import "../../../../shared/components/UniversalEditModal/UniversalEditModal.scss";
import {
    displayCloseModalDialog,
    displayNotSaved,
    displaySuccessUpdate,
    displayError,
} from "../../../../shared/services/Notifications/NotificationServices";
import type {
    RegionType,
    UserDataType as DataType,
} from "../../../../shared/types/EditModal/types";
import {
    useState,
    useEffect,
    Dispatch,
    SetStateAction,
    useCallback,
    useMemo,
} from "react";
import { useForm, SubmitHandler, FormProvider } from "react-hook-form";
import IdentifierTab from "../../../../shared/components/UniversalTabs/IdentifiedTab";
import AccountTab from "../../../../shared/components/UniversalTabs/AccountTab";
import UniversalEditModal from "../../../../shared/components/UniversalEditModal/UniversalEditModal";
import useImageUpload from "../../../../shared/components/UniversalEditModal/hooks/useImageUpload";
import { useSearchParams } from "react-router";
import useUserProfile from "./useUserProfile";
import UserBasicInfo from "../../../../shared/components/UniversalTabs/BasicInfo/UserBasicInfo";
import UserShowcaseTab from "../../../../shared/components/UniversalTabs/Showcase/UserShowcaseTab";
import UserPrivacyTab from "../../../../shared/components/UniversalTabs/PrivacyTab/UserPrivacyTab";
import { regionMap } from "../../../../shared/components/UniversalEditModal/utils/common";
import { AvailableRegionName } from "../../../../shared/types/EditModal/types";
import { getLogin } from "../../../Login/services/authService";
export default function ProfileEditModal({
    isOpen,
    setIsOpen,
}: {
    isOpen: boolean;
    setIsOpen: Dispatch<SetStateAction<boolean>>;
}) {
    const username = getLogin() || "";

    const {
        fetchStatistics,
        fetchRegions,
        fetchUserData,
        patchUserData,
        uploadUserAvatar,
        uploadUserTheme,
        fetchSpecialisations,
        fetchArmoryList,
    } = useUserProfile(username);

    const [searchParams, setSearchParams] = useSearchParams();
    const tabFromUrl = searchParams.get("tab");

    const [activeTab, setActiveTab] = useState(tabFromUrl || "basic");
    const [teams, setTeams] = useState<{ name: string; slug: string }[]>();
    const [openPassField, setOpenPassField] = useState<
        "password" | "newPassword" | "confirmPassword" | null
    >(null);
    const [fullArmoryList, setFullArmoryList] = useState<
        {
            id: string;
            firm_name: string;
        }[]
    >([]);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [regions, setRegions] = useState<RegionType[]>([]);

    const queryKey = ["userProfile", username, "profile"];

    const avatarUpload = useImageUpload(
        uploadUserAvatar,
        "../modal/avatar.svg",
        queryKey,
    );

    const themeUpload = useImageUpload(
        uploadUserTheme,
        "../modal/theme.png",
        queryKey,
    );

    const {
        imageUrl: avatarUrl,
        setImageUrl: setAvatarUrl,
        fileInputRef: avatarInputRef,
        handleFileChange: handleAvatarChange,
        uploadFile: handleAvatarUpload,
    } = avatarUpload;

    const {
        imageUrl: themeUrl,
        setImageUrl: setThemeUrl,
        fileInputRef: themeInputRef,
        handleFileChange: handleThemeChange,
    } = themeUpload;

    const formHooks = useForm({ mode: "onChange" });

    useEffect(() => {
        formHooks.register("camouflagesList");
    }, [formHooks.register]);

    const onSubmit: SubmitHandler<any> = async (formData) => {
        const selectedArmory = fullArmoryList.find(
            (armory) => armory.firm_name === formData.selectedArmory,
        );
        const changed: Partial<DataType> = {};
        Object.keys(formHooks.formState.dirtyFields).forEach(
            (k) => ((changed as any)[k] = formData[k]),
        );
        changed.weapon_id = selectedArmory?.id;
        if (changed.region_name) {
            changed.region = {
                name: changed.region_name,
                ru_code: regionMap.get(changed.region_name as AvailableRegionName) || 0,
            };
        }

        if (!Object.keys(changed).length) return;
        try {
            await patchUserData(changed, teams);
            displaySuccessUpdate();
            const userRes = await fetchUserData();
            formHooks.reset({
                ...formData,
                email: userRes.data.email
            }, { keepDirty: false });
            setHasUnsavedChanges(false);
        } catch (error: unknown) {
            displayError((error as Error).toString());
        }
    };

    useEffect(() => {
        const load = async () => {
            const userRes = await fetchUserData();

            setAvatarUrl(
                userRes.data?.avatar_link
                    ? `${userRes.data.avatar_link}`
                    : "../modal/avatar.svg",
            );

            setThemeUrl(
                userRes.data?.banner_link
                    ? `${userRes.data.banner_link}`
                    : "../modal/theme.png",
            );
            formHooks.reset(userRes.data, { keepDirty: false });

            const [statsRes, regRes, specialisationsRes, armoryListRes] = await Promise.all([
                fetchStatistics(),
                fetchRegions(),
                fetchSpecialisations(),
                fetchArmoryList(username)
            ]);

            setFullArmoryList(armoryListRes.data);
            const armoryNames = armoryListRes.data.map((a: any) => a.firm_name);
            const profileTeam = userRes.data.teams?.find(
                (t: { slug: string; name: string; is_profile_team: boolean }) =>
                    t.is_profile_team,
            );
            const armory = armoryListRes.data?.find(
                (t: {
                    firm_name: string;
                    id: string;
                    is_profile_weapon: boolean;
                }) => t.is_profile_weapon === true,
            )?.firm_name;
            const init = {
                ...userRes.data,
                email_address: userRes.data.email.address || "",
                email_status: userRes.data.email.is_confirmed || false,
                region_name: userRes.data.region?.name || "",
                experience: statsRes.data.experience ? 2026 - Number(statsRes.data.experience) : null,
                team_slug: profileTeam?.name || "",
                armoryList: armoryNames,
                specList: specialisationsRes.data.map(
                    (c: { name: string }) => c.name,
                ),
                armory,
                registered_by: userRes.data.card?.registered_by?.name,
                registered_by_slug: userRes.data.card?.registered_by?.slug,
                datetime_registered: userRes.data.card?.datetime_registered,
                main_spec_name: userRes.data.main_spec,
            };
            formHooks.reset(init, { keepDirty: false });
            setTeams(userRes.data.teams);

            setRegions(regRes.data);
            setHasUnsavedChanges(false);
        };
        load();
    }, [formHooks.reset]);

    useEffect(() => {
        const subscribtion = formHooks.watch(({ name }) => {
            if (name === "password") return;

            const dirtyFields = formHooks.formState.dirtyFields;
            const hasRealChanges = Object.keys(dirtyFields).some(
                (key) => key !== "password" && dirtyFields[key],
            );

            setHasUnsavedChanges(hasRealChanges);
        });

        return () => subscribtion.unsubscribe();
    }, [formHooks]);

    useEffect(() => {
        const urlTab = searchParams.get("tab");
        if (urlTab) setActiveTab(urlTab);
    }, [searchParams]);

    const togglePasswordVisibility = (field: typeof openPassField) =>
        setOpenPassField((prev) => (prev === field ? null : field));

    const blockIfUnsaved = useCallback(() => {
        const values = formHooks.getValues();
        const hasInput = Object.values(values).some((v) => v && v !== "");
        return hasUnsavedChanges && hasInput;
    }, [formHooks, hasUnsavedChanges]);

    const closeModal = useCallback(() => {
        if (blockIfUnsaved()) {
            displayCloseModalDialog(() => setIsOpen(false));
            return;
        }
        setIsOpen(false);
    }, [blockIfUnsaved]);

    const changeActiveTab = useCallback(
        (s: string) => {
            if (blockIfUnsaved()) {
                displayNotSaved();
                return;
            }
            setActiveTab(s);
            setSearchParams((prev) => {
                const newParams = new URLSearchParams(prev);
                if (s) newParams.set("tab", s);
                else newParams.delete("tab");
                return newParams;
            });
        },
        [blockIfUnsaved, setSearchParams],
    );

    const handleFile = useCallback(
        (type: "avatar" | "theme") =>
            type === "avatar" ? handleAvatarChange : handleThemeChange,
        [handleAvatarChange, handleThemeChange],
    );

    const tabs = useMemo(
        () => [
            {
                id: "basic",
                label: "Основная информация",
                component: (
                    <UserBasicInfo
                        regions={regions}
                        hasUnsavedChanges={hasUnsavedChanges}
                    />
                ),
            },
            {
                id: "identifier",
                label: "Идентификатор",
                component: <IdentifierTab />,
            },
            {
                id: "showcase",
                label: "Витрина",
                component: (
                    <UserShowcaseTab
                        teamList={
                            (teams && teams.map((t: any) => t.name)) || []
                        }
                        hasUnsavedChanges={hasUnsavedChanges}
                    />
                ),
            },
            {
                id: "account",
                label: "Аккаунт",
                component: (
                    <AccountTab
                        openField={openPassField}
                        toggle={togglePasswordVisibility}
                        hasUnsavedChanges={hasUnsavedChanges}
                    />
                ),
            },
            {
                id: "privacy",
                label: "Конфиденциальность",
                component: (
                    <UserPrivacyTab hasUnsavedChanges={hasUnsavedChanges} />
                ),
            },
            // {
            //   id: 'settings',
            //   label: 'Настройки',
            //   component: (
            //     <SettingsTab />
            //   )
            // }
        ],
        [regions, teams, hasUnsavedChanges],
    );

    return (
        <>
            <FormProvider {...formHooks}>
                <UniversalEditModal
                    isOpen={isOpen}
                    closeModal={closeModal}
                    themeUrl={themeUrl}
                    avatarUrl={avatarUrl}
                    themeInputRef={themeInputRef}
                    avatarInputRef={avatarInputRef}
                    handleFile={handleFile}
                    handleAvatarUpload={handleAvatarUpload}
                    activeTab={activeTab}
                    changeActiveTab={changeActiveTab}
                    handleSubmit={formHooks.handleSubmit}
                    onSubmit={onSubmit}
                    tabs={tabs}
                />
            </FormProvider>
        </>
    );
}
